interface RasterConfig {
  stripWidth: number;      // 光栅条纹宽度（像素）
  angle?: number;          // 光栅角度（度）
  offset?: number;         // 偏移量
  blendMode?: 'alternate' | 'gradient' | 'sine'; // 混合模式
}

interface ImageData {
  url: string;
  blob?: Blob;
  canvas?: HTMLCanvasElement;
}

interface PrintConfig {
  paperSize?: 'A4' | 'A3' | 'Letter' | 'Custom';
  customSize?: { width: number; height: number }; // 自定义纸张尺寸（毫米）
  orientation?: 'portrait' | 'landscape';
  quality?: 'draft' | 'normal' | 'high';
  photoAspectRatio?: '1:1' | '3:4' | string; // 添加照片宽高比配置
  margins?: {
    top: number;
    right: number;
    bottom: number;
    left: number;
  };
}

interface WatermarkOptions {
  width: number;
  height: number;
  logoPath?: string;
  ctx?: CanvasRenderingContext2D;
  position?: "bottom-left" | "bottom-right";
  opacity?: number;
  widthRatio?: number; // 水印宽度占画布宽度的比例
}

// 检测是否在Electron环境中运行
const isElectron = (): boolean => {
  // 检查window上是否有electron对象
  if (typeof window !== 'undefined' && 
      typeof window.process === 'object') {
    // @ts-ignore - Electron 特定属性
    if (window.process.type === 'renderer' || window.process.__nwjs) {
      return true;
    }
  }

  // 检查navigator.userAgent
  if (typeof navigator === 'object' && 
      typeof navigator.userAgent === 'string' && 
      navigator.userAgent.indexOf('Electron') >= 0) {
    return true;
  }

  // 检查是否有electron IPC对象
  // @ts-ignore - Electron 特定属性
  return window && window.electron !== undefined;
};

interface WatermarkConfig {
  bottomLeft: {
    enabled: boolean;
    logoPath: string;
  };
  bottomRightA: {
    enabled: boolean;
    logoPath: string;
  };
  bottomRightB: {
    enabled: boolean;
    logoPath: string;
  };
}

class ImageService {
  private canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;
  private watermarkSettings: WatermarkConfig;
  
  // 打印常量
  private static readonly PRINT_DPI = 600;           // 600 DPI
  private static readonly PRINT_SIZE_MM = 80;        // 80mm x 80mm
  private static readonly MM_TO_INCH = 25.4;         // 毫米转英寸的转换系数

  constructor(config?: any) {
    this.canvas = document.createElement('canvas');
    this.ctx = this.canvas.getContext('2d')!;
    
    // 初始化水印设置
    this.watermarkSettings = this.initializeWatermarkSettings(config);
  }

  /**
   * 从配置初始化水印设置
   */
  private initializeWatermarkSettings(config?: any): WatermarkConfig {
    // 默认设置
    const defaultSettings: WatermarkConfig = {
      bottomLeft: {
        enabled: false,
        logoPath: '/logo-front-urumoqi.png',
      },
      bottomRightA: {
        enabled: false,
        logoPath: '/logo-part1.png',
      },
      bottomRightB: {
        enabled: false,
        logoPath: '/logo-part2.png',
      },
    };

    if (!config) {
      return defaultSettings;
    }

    // 加前缀辅助函数
    const prefixUrl = (url?: string, fallback?: string) => {
      if (url) {
        return `${process.env.REACT_APP_BACKEND_URL}${url}`;
      }
      return fallback || '';
    };

    // 从config中解析水印设置
    return {
      bottomLeft: {
        enabled: !!config.watermark_bottom_left,
        logoPath: prefixUrl(config.watermark_bottom_left?.url, defaultSettings.bottomLeft.logoPath),
      },
      bottomRightA: {
        enabled: !!config.watermark_bottom_right_a,
        logoPath: prefixUrl(config.watermark_bottom_right_a?.url, defaultSettings.bottomRightA.logoPath),
      },
      bottomRightB: {
        enabled: !!config.watermark_bottom_right_b,
        logoPath: prefixUrl(config.watermark_bottom_right_b?.url, defaultSettings.bottomRightB.logoPath),
      },
    };
  }

  /**
   * 更新水印设置
   */
  public updateWatermarkSettings(config: any): void {
    this.watermarkSettings = this.initializeWatermarkSettings(config);
  }

  /**
   * 计算打印尺寸的像素值
   * @param sizeInMm 毫米尺寸
   * @param dpi DPI值
   * @returns 像素值
   */
  private static calculatePrintPixels(sizeInMm: number, dpi: number = ImageService.PRINT_DPI): number {
    const sizeInInches = sizeInMm / ImageService.MM_TO_INCH;
    return Math.round(sizeInInches * dpi);
  }

  /**
   * 获取标准打印尺寸（像素）
   * @returns {width: number, height: number} 80mm x 80mm at 300 DPI的像素尺寸
   */
  static getPrintDimensions(): { width: number; height: number } {
    const pixels = ImageService.calculatePrintPixels(ImageService.PRINT_SIZE_MM);
    return { width: pixels, height: pixels };
  }

  /**
   * 获取打印规格信息
   * @returns 打印规格详细信息
   */
  static getPrintSpecifications() {
    const dimensions = ImageService.getPrintDimensions();
    return {
      dpi: ImageService.PRINT_DPI,
      sizeInMm: ImageService.PRINT_SIZE_MM,
      sizeInPixels: dimensions,
      sizeInInches: ImageService.PRINT_SIZE_MM / ImageService.MM_TO_INCH,
      description: `${ImageService.PRINT_SIZE_MM}mm x ${ImageService.PRINT_SIZE_MM}mm at ${ImageService.PRINT_DPI} DPI (${dimensions.width} x ${dimensions.height} pixels)`
    };
  }


  /**
   * 光栅合成两张图片
   * @param image1 第一张图片数据
   * @param image2 第二张图片数据
   * @param config 光栅配置参数
   * @returns 合成后的图片数据URL
   */
  async rasterComposite(
    image1: ImageData,
    image2: ImageData,
    config: RasterConfig
  ): Promise<string> {
    try {
      // 加载两张图片
      const img1 = await this.loadImage(image1.url);
      const img2 = await this.loadImage(image2.url);

      // 获取标准打印尺寸（80mm x 80mm at 300 DPI）
      const { width: printWidth, height: printHeight } = ImageService.getPrintDimensions();
      // console.log(`打印尺寸: ${printWidth} x ${printHeight} pixels`);

      this.canvas.width = printWidth;
      this.canvas.height = printHeight;

      // 清空画布
      this.ctx.clearRect(0, 0, printWidth, printHeight);

      // 绘制基础图层，调整到打印尺寸
      this.ctx.drawImage(img1, 0, 0, printWidth, printHeight);

      if (this.watermarkSettings.bottomRightA.enabled) {
        // 添加水印
        await this.applyWatermark({
          width: printWidth,
          height: printHeight,
          logoPath: this.watermarkSettings.bottomRightA.logoPath,
        });
      }

      // 根据光栅模式进行合成
      await this.applyRasterEffect(img2, printWidth, printHeight, config);

      if (this.watermarkSettings.bottomLeft.enabled) {
        // 添加水印
        await this.applyWatermark({
          width: printWidth,
          height: printHeight,
          logoPath: this.watermarkSettings.bottomLeft.logoPath,
          position: 'bottom-left',
          widthRatio: 0.25,
        });
      }

      // 返回合成结果，使用最高质量
      return this.canvas.toDataURL('image/jpeg', 1.0);
    } catch (error) {
      console.error('光栅合成失败:', error);
      if (error instanceof Error) {
        throw new Error(`图片光栅合成失败: ${error.message}`);
      } else {
        throw new Error('图片光栅合成失败：未知错误');
      }
    }
  }
  
  /**
   * 添加水印到当前画布
   */
  private async applyWatermark(options: WatermarkOptions): Promise<void> {
    try {
      const {
        width, 
        height, 
        logoPath = '/logo.png', 
        ctx = this.ctx, 
        position = 'bottom-right',
        opacity = 1.0,
        widthRatio = 0.4
      } = options;


      // 从assets文件夹加载logo水印
      const watermark = await this.loadImage(logoPath);

      // 计算水印尺寸，保持一定比例（例如图像宽度的80%）
      const watermarkWidth = Math.round(width * widthRatio);
      const watermarkHeight = Math.round(watermarkWidth * (watermark.height / watermark.width));

      // 定位到右下角，留一定边距
      const padding = Math.round(width * 0.02);

      let [x,y] = [0, 0]; // 默认位置
      switch( position) {
        case 'bottom-right':
          // 设置水印位置为右下角
          x = width - watermarkWidth - padding;
          y = height - watermarkHeight - padding;
          break;
        case 'bottom-left':
          // 设置水印位置为左下角
          x = padding;
          y = height - watermarkHeight - padding;
          break;
        default:
          // 默认位置为右下角
          x = width - watermarkWidth - padding;
          y = height - watermarkHeight - padding;
      }

      // 设置水印透明度
      ctx.globalAlpha = opacity; 

      // 绘制水印
      ctx.drawImage(watermark, x, y, watermarkWidth, watermarkHeight);

      // 恢复透明度
      ctx.globalAlpha = 1.0;
    } catch (error) {
      console.warn('添加水印失败，继续处理光栅合成:', error);
    }
  }

  /**
   * 应用光栅效果
   */
  private async applyRasterEffect(
    img2: HTMLImageElement,
    width: number,
    height: number,
    config: RasterConfig
  ): Promise<void> {
    const { stripWidth, angle = 0, offset = 0, blendMode = 'alternate' } = config;

    // 创建临时画布用于第二张图片
    const tempCanvas = document.createElement('canvas');
    const tempCtx = tempCanvas.getContext('2d')!;
    tempCanvas.width = width;
    tempCanvas.height = height;

    // 绘制第二张图片到临时画布
    tempCtx.drawImage(img2, 0, 0, width, height);

    if (this.watermarkSettings.bottomRightB.enabled) {
      // 添加水印
      await this.applyWatermark({
        width,
        height,
        logoPath: this.watermarkSettings.bottomRightB.logoPath,
        ctx: tempCtx
      });
    }

    // 根据混合模式应用光栅效果
    switch (blendMode) {
      case 'alternate':
        this.applyAlternateRaster(tempCanvas, width, height, stripWidth, angle, offset);
        break;
      case 'gradient':
        this.applyGradientRaster(tempCanvas, width, height, stripWidth, angle, offset);
        break;
      case 'sine':
        this.applySineRaster(tempCanvas, width, height, stripWidth, angle, offset);
        break;
    }
  }

  /**
   * 交替光栅模式
   */
  private applyAlternateRaster(
    tempCanvas: HTMLCanvasElement,
    width: number,
    height: number,
    stripWidth: number,
    angle: number,
    offset: number
  ): void {
    const tempCtx = tempCanvas.getContext('2d')!;
    const imageData = tempCtx.getImageData(0, 0, width, height);
    const data = imageData.data;

    if (angle === 0 && offset === 0) {
      // 如果没有旋转和偏移，直接处理条纹
      for (let y = 0; y < height; y++) {
        for (let x = 0; x < width; x++) {
          // 计算光栅位置
          const stripPosition = Math.floor(x / stripWidth) % 2;
          
          const pixelIndex = (y * width + x) * 4;
          
          // 如果在奇数条纹上，则使第二张图片透明
          if (stripPosition === 0) {
            data[pixelIndex + 3] = 0; // 设置透明度为0
          }
        }
      }
    } else {
      for (let y = 0; y < height; y++) {
        for (let x = 0; x < width; x++) {
          // 计算旋转后的坐标
          const rotatedX = this.rotateX(x, y, angle, width / 2, height / 2);
          
          // 计算光栅位置
          const stripPosition = Math.floor((rotatedX + offset) / stripWidth) % 2;
          
          const pixelIndex = (y * width + x) * 4;
          
          // 如果在奇数条纹上，则使第二张图片透明
          if (stripPosition === 0) {
            data[pixelIndex + 3] = 0; // 设置透明度为0
          }
        }
      }
    }

    tempCtx.putImageData(imageData, 0, 0);
    
    // 将处理后的图片叠加到主画布
    this.ctx.globalCompositeOperation = 'source-over';
    this.ctx.drawImage(tempCanvas, 0, 0);
  }

  /**
   * 渐变光栅模式
   */
  private applyGradientRaster(
    tempCanvas: HTMLCanvasElement,
    width: number,
    height: number,
    stripWidth: number,
    angle: number,
    offset: number
  ): void {
    const tempCtx = tempCanvas.getContext('2d')!;
    const imageData = tempCtx.getImageData(0, 0, width, height);
    const data = imageData.data;

    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        const rotatedX = this.rotateX(x, y, angle, width / 2, height / 2);
        
        // 计算在条纹内的相对位置 (0-1)
        const stripPosition = ((rotatedX + offset) % stripWidth) / stripWidth;
        
        // 创建渐变透明度
        const alpha = Math.abs(Math.sin(stripPosition * Math.PI));
        
        const pixelIndex = (y * width + x) * 4;
        data[pixelIndex + 3] = Math.floor(data[pixelIndex + 3] * alpha);
      }
    }

    tempCtx.putImageData(imageData, 0, 0);
    this.ctx.globalCompositeOperation = 'source-over';
    this.ctx.drawImage(tempCanvas, 0, 0);
  }

  /**
   * 正弦波光栅模式
   */
  private applySineRaster(
    tempCanvas: HTMLCanvasElement,
    width: number,
    height: number,
    stripWidth: number,
    angle: number,
    offset: number
  ): void {
    const tempCtx = tempCanvas.getContext('2d')!;
    const imageData = tempCtx.getImageData(0, 0, width, height);
    const data = imageData.data;

    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        const rotatedX = this.rotateX(x, y, angle, width / 2, height / 2);
        
        // 使用正弦波计算透明度
        const frequency = (2 * Math.PI) / stripWidth;
        const alpha = (Math.sin((rotatedX + offset) * frequency) + 1) / 2;
        
        const pixelIndex = (y * width + x) * 4;
        data[pixelIndex + 3] = Math.floor(data[pixelIndex + 3] * alpha);
      }
    }

    tempCtx.putImageData(imageData, 0, 0);
    this.ctx.globalCompositeOperation = 'source-over';
    this.ctx.drawImage(tempCanvas, 0, 0);
  }

  /**
   * 计算旋转后的X坐标
   */
  private rotateX(x: number, y: number, angle: number, centerX: number, centerY: number): number {
    const rad = (angle * Math.PI) / 180;
    const cos = Math.cos(rad);
    const sin = Math.sin(rad);
    
    const dx = x - centerX;
    const dy = y - centerY;
    
    return dx * cos - dy * sin + centerX;
  }

  /**
   * 加载图片
   */
  private loadImage(src: string): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      
      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error(`Failed to load image: ${src}`));
      
      img.src = src;
    });
  }

  /**
   * 预设光栅配置
   */
  static getPresetConfigs() {
    return {
      // 标准立体光栅 120线
      S120: {
        stripWidth: 6,
        angle: 0,
        offset: 0,
        blendMode: 'alternate' as const
      },

      // 标准立体光栅 50线
      S50: {
        stripWidth: 3,
        angle: 0,
        offset: 0,
        blendMode: 'alternate' as const
      },
      
      // 细条纹光栅 40线
      S40: {
        stripWidth: 3.74,
        angle: 0,
        offset: 0,
        blendMode: 'alternate' as const
      },

      // 渐变光栅
      gradient: {
        stripWidth: 12,
        angle: 0,
        offset: 0,
        blendMode: 'gradient' as const
      },
      
      // 正弦波光栅
      sine: {
        stripWidth: 10,
        angle: 0,
        offset: 0,
        blendMode: 'sine' as const
      },
      
      // 倾斜光栅
      tilted: {
        stripWidth: 8,
        angle: 15,
        offset: 0,
        blendMode: 'alternate' as const
      }
    };
  }

  /**
   * 批量处理多个光栅配置
   */
  async batchRasterComposite(
    image1: ImageData,
    image2: ImageData,
    configs: RasterConfig[]
  ): Promise<string[]> {
    const results: string[] = [];
    
    for (const config of configs) {
      try {
        const result = await this.rasterComposite(image1, image2, config);
        results.push(result);
      } catch (error) {
        console.error('批量处理失败:', error);
        results.push('');
      }
    }
    
    return results;
  }

  /**
   * 获取光栅预览（较小尺寸用于快速预览）
   */
  async getRasterPreview(
    image1: ImageData,
    image2: ImageData,
    config: RasterConfig,
    previewSize: { width: number; height: number } = { width: 200, height: 200 }
  ): Promise<string> {
    const originalCanvas = this.canvas;
    const originalCtx = this.ctx;
    
    // 创建预览画布
    this.canvas = document.createElement('canvas');
    this.ctx = this.canvas.getContext('2d')!;
    
    try {
      // 加载两张图片
      const img1 = await this.loadImage(image1.url);
      const img2 = await this.loadImage(image2.url);

      // 使用预览尺寸
      this.canvas.width = previewSize.width;
      this.canvas.height = previewSize.height;

      // 清空画布
      this.ctx.clearRect(0, 0, previewSize.width, previewSize.height);

      // 绘制基础图层
      this.ctx.drawImage(img1, 0, 0, previewSize.width, previewSize.height);

      // 根据光栅模式进行合成
      await this.applyRasterEffect(img2, previewSize.width, previewSize.height, config);

      // 返回预览结果
      return this.canvas.toDataURL('image/jpeg', 0.8);
    } catch (error) {
      console.error('光栅预览失败:', error);
      throw new Error('生成预览失败');
    } finally {
      // 恢复原始画布
      this.canvas = originalCanvas;
      this.ctx = originalCtx;
    }
  }

  /**
   * 静默打印图片
   */
  async silentPrint(imageUrl: string): Promise<void> {
    return this.silentPrintWithConfig(imageUrl);
  }

  /**
   * 静默打印图片（带配置选项）
   */
  async silentPrintWithConfig(imageUrl: string, config: PrintConfig = {}): Promise<void> {
    const {
      paperSize = 'Letter',
      customSize,
      orientation = 'landscape',
      quality = 'high',
      photoAspectRatio = '1:1',
      margins = { top: 0, right: 0, bottom: 0, left: 0 }
    } = config;

    // 检查是否在Electron环境中
    if (isElectron()) {
      try {
        console.log('在Electron环境中，使用Electron打印API');
        
        // 确保图像是data URL格式
        let printImageData = imageUrl;
        
        if (!imageUrl.startsWith('data:')) {
          // 如果不是data URL，则尝试转换成base64
          printImageData = await this.urlToDataURL(imageUrl);
        }

        // 通过IPC发送到Electron主进程进行打印
        // @ts-ignore - Electron对象在TS类型定义中不存在
        try {
          // 调用Electron的IPC打印方法
          // @ts-ignore - Electron对象在TS类型定义中不存在
          window.electronAPI?.printPhoto(printImageData, config);
          return;
        } catch (e) {
          console.error('Electron IPC调用失败:', e);
          throw new Error('无法调用Electron打印方法，请检查配置');
        }
      } catch (error) {
        console.error('Electron打印失败:', error);
        throw new Error('打印失败，请重试');
      }
    }

    // 如果不是Electron环境或者Electron打印失败，则使用浏览器打印
    // Convert data URL to Blob URL if it's a data URL
    let printImageUrl = imageUrl;
    
    if (imageUrl.startsWith('data:')) {
      try {
        const blob = this.dataURLToBlob(imageUrl);
        printImageUrl = URL.createObjectURL(blob);
      } catch (e) {
        console.error('Failed to convert data URL to Blob:', e);
      }
    }

    try {
      // 创建隐藏的 iframe 用于打印
      const printFrame = document.createElement('iframe');
      printFrame.style.position = 'fixed';
      printFrame.style.top = '-1000mm';
      printFrame.style.left = '-1000mm';
      printFrame.style.width = '160mm'; 
      printFrame.style.height = '160mm';
      printFrame.style.zIndex = "0";
      
      document.body.appendChild(printFrame);
      
      // 等待 iframe 加载完成
      await new Promise<void>((resolve) => {
        printFrame.onload = () => resolve();
        printFrame.src = 'about:blank';
      });
      
      const printDoc = printFrame.contentDocument || printFrame.contentWindow?.document;
      if (!printDoc) {
        throw new Error('无法访问打印文档');
      }
      
      // 根据照片比例计算尺寸
      let photoWidth: string, photoHeight: string;
      let paperSizeSpec: string;
      
      // 设置纸张尺寸规格
      if (paperSize === 'Custom' && customSize) {
        paperSizeSpec = `${customSize.width}mm ${customSize.height}mm`;
        // 对于自定义尺寸（如100mm x 153mm照片纸），直接使用纸张尺寸填满整张纸
        photoWidth = `${customSize.width}mm`;
        photoHeight = `${customSize.height}mm`;
      } else {
        paperSizeSpec = `${paperSize} ${orientation}`;
        
        if (photoAspectRatio === '3:4') {
          // 3:4 比例，保持宽度60mm，高度80mm
          photoWidth = '60mm';
          photoHeight = '80mm';
        } else if (photoAspectRatio === '1:1') {
          // 1:1 比例，80mm x 80mm
          photoWidth = '80mm';
          photoHeight = '80mm';
        } else {
          // 自定义比例处理
          const [width, height] = photoAspectRatio.split(':').map(Number);
          if (width && height) {
            const baseSize = 80; // 基础尺寸 80mm
            if (width > height) {
              photoWidth = `${baseSize}mm`;
              photoHeight = `${(baseSize * height / width)}mm`;
            } else {
              photoWidth = `${(baseSize * width / height)}mm`;
              photoHeight = `${baseSize}mm`;
            }
          } else {
            // 默认正方形
            photoWidth = '80mm';
            photoHeight = '80mm';
          }
        }
      }
      
      // 构建打印页面 HTML with configuration
      const printHTML = `
        <!DOCTYPE html>
        <html>
        <head>
          <title>打印照片</title>
          <style>
            @page {
              margin: ${margins.top}mm ${margins.right}mm ${margins.bottom}mm ${margins.left}mm;
              size: ${paperSizeSpec};
            }
            html, body {
              margin: 0;
              padding: 0;
              display: flex;
              justify-content: center;
              align-items: center;
              min-height: 100vh;
              background: white;
            }
            .photo-container {
              width: ${photoWidth};
              height: ${photoHeight};
              box-sizing: border-box;
              overflow: hidden;
              /* Add border for debugging - remove in production */
              /* border: 0.25pt solid #ddd; */
            }
            img {
              width: ${photoWidth};
              height: ${photoHeight};
              object-fit: cover; /* 使用cover确保填满容器并保持比例 */
              ${quality === 'high' ? 'image-rendering: -webkit-optimize-contrast;' : ''}
              ${quality === 'draft' ? 'image-rendering: pixelated;' : ''}
            }
            @media print {
              @page {
                size: ${paperSizeSpec};
                margin: ${margins.top}mm ${margins.right}mm ${margins.bottom}mm ${margins.left}mm;
              }
              html {
                zoom: 1.0; /* 使用1.0缩放以确保精确尺寸 */
              }
              body {
                -webkit-print-color-adjust: exact;
                print-color-adjust: exact;
                /* Force size calibration */
                transform-origin: top left;
                transform: scale(1);
              }
              .photo-container {
                width: ${photoWidth} !important;
                height: ${photoHeight} !important;
              }
              img {
                width: ${photoWidth} !important;
                height: ${photoHeight} !important;
                object-fit: cover !important;
                transform: scale(1) !important;
                max-width: none !important;
                max-height: none !important;
              }
            }
          </style>
        </head>
        <body>
          <div class="photo-container">
            <img src="${printImageUrl}" alt="光栅合成照片" />
          </div>
          <script>
            // Script to ensure image dimensions are locked
            window.onbeforeprint = function() {
              const img = document.querySelector('img');
              console.log('Before print image size:', img.offsetWidth, 'x', img.offsetHeight);
            };
          </script>
        </body>
        </html>
      `;
      
      printDoc.open();
      printDoc.write(printHTML);
      printDoc.close();
      
      // 等待图片加载完成
      const img = printDoc.querySelector('img') as HTMLImageElement;
      if (img) {
        await new Promise<void>((resolve, reject) => {
          img.onerror = () => reject(new Error('图片加载失败'));
          resolve();
        });
      }
      
      // 短暂延迟确保渲染完成
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // 执行打印
      const printWindow = printFrame.contentWindow;
      if (printWindow) {
        printWindow.focus();
        printWindow.print();
        
        // 等待打印对话框关闭后清理
        setTimeout(() => {
          document.body.removeChild(printFrame);
        }, 1000);
      }
    } catch (error) {
      console.error('配置化打印失败:', error);
      throw new Error('打印失败，请重试');
    }
  }

  /**
   * 转换 data URL 为 Blob
   */
  dataURLToBlob(dataURL: string): Blob {
    const arr = dataURL.split(',');
    const mime = arr[0].match(/:(.*?);/)![1];
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    
    return new Blob([u8arr], { type: mime });
  }

  /**
   * 将URL转换为data URL
   */
  urlToDataURL(url: string): Promise<string> {
    return new Promise<string>((resolve, reject) => {
      // 如果已经是data URL，直接返回
      if (url.startsWith('data:')) {
        resolve(url);
        return;
      }

      // 创建临时画布来转换图像
      const tempCanvas = document.createElement('canvas');
      const tempCtx = tempCanvas.getContext('2d')!;
      const img = new Image();
      
      img.crossOrigin = 'anonymous'; // 避免跨域问题
      img.onerror = () => reject(new Error(`无法加载图片: ${url}`));
      
      img.onload = () => {
        // 设置画布尺寸以匹配图像
        tempCanvas.width = img.width;
        tempCanvas.height = img.height;
        
        // 绘制图像到画布
        tempCtx.drawImage(img, 0, 0);
        
        // 转换为data URL
        try {
          const dataURL = tempCanvas.toDataURL('image/jpeg', 1.0);
          resolve(dataURL);
        } catch (e) {
          reject(new Error(`转换图片为data URL失败: ${e}`));
        }
      };
      
      // 加载图片
      img.src = url;
    });
  }
}

// 创建工厂函数来生成ImageService实例
export const createImageService = (config?: any): ImageService => {
  return new ImageService(config);
};

// 创建默认实例（为了向后兼容）
export const imageService = new ImageService();

// 导出类型和类
export type { RasterConfig, ImageData, PrintConfig, WatermarkConfig };
export { ImageService };